import { Layers, ShieldCheck, Sigma, Workflow } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { stronomModules } from "@/data/stronom-modules";

const governancePillars = [
  {
    title: "Structure",
    description: "Unified language for orchestrating workflows, policies, and decision points across every module.",
    icon: Workflow,
  },
  {
    title: "Governance",
    description: "Policy-aware orchestration with role-based controls, approvals, and escalation paths integrated into the core.",
    icon: ShieldCheck,
  },
  {
    title: "Proof",
    description: "Immutable evidence engine recording context, inputs, and outputs for complete compliance visibility.",
    icon: Sigma,
  },
  {
    title: "Trust",
    description: "Cryptographically signed events, verifiable telemetry, and transparent audit trails for every stakeholder.",
    icon: Layers,
  },
];

const Core = () => {
  const coreModule = stronomModules.find((module) => module.key === "core");
  const accentColor = coreModule ? `hsl(var(${coreModule.accentVar}))` : "#808080";

  return (
    <div className="relative overflow-hidden">
      <div className="pointer-events-none absolute -top-32 left-1/2 h-[420px] w-[420px] -translate-x-1/2 rounded-full" style={{ background: accentColor, filter: "blur(200px)", opacity: 0.35 }} />
      <div className="pointer-events-none absolute -bottom-40 right-[-12%] h-[360px] w-[360px] rounded-full" style={{ background: accentColor, filter: "blur(210px)", opacity: 0.3 }} />

      <section className="relative px-6 pb-24 pt-20">
        <div className="mx-auto max-w-5xl space-y-8">
          <Badge variant="outline" className="border-border/60 bg-background/80 text-muted-foreground">
            Framework Foundation
          </Badge>
          <h1 className="font-display text-4xl font-semibold text-foreground md:text-5xl">STRONOM Core</h1>
          <p className="max-w-3xl text-lg text-muted-foreground">
            STRONOM Core is the universal rule fabric powering every module. It harmonizes structured logic, policy,
            and verification services into a single governance layer that keeps intelligent systems predictable and
            provable.
          </p>

          <div className="grid gap-6 rounded-3xl border border-border/60 bg-card/80 p-8 backdrop-blur md:grid-cols-2">
            <div className="space-y-3 text-sm text-muted-foreground">
              <h2 className="text-lg font-semibold text-foreground">What Core Provides</h2>
              <p>• Declarative rule engine with versioning and approvals</p>
              <p>• Policy orchestration with hierarchical inheritance</p>
              <p>• Evidence ledger for every decision, action, and output</p>
              <p>• Telemetry mesh for observability and anomaly detection</p>
            </div>
            <div className="space-y-3 text-sm text-muted-foreground">
              <h2 className="text-lg font-semibold text-foreground">Why it matters</h2>
              <p>
                Creative, operational, and governance teams collaborate on shared logic. STRONOM Core ensures every
                module behaves consistently, respects constraints, and produces compliance-ready deliverables.
              </p>
              <p>
                The framework integrates with your existing systems, allowing staged adoption and phased rollout across
                teams and regions.
              </p>
            </div>
          </div>

          <div className="mt-12 grid gap-6 rounded-3xl border border-border/60 bg-card/80 p-8 backdrop-blur md:grid-cols-2">
            {governancePillars.map((pillar) => (
              <div key={pillar.title} className="flex gap-4 rounded-2xl border border-border/50 bg-background/70 p-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-border/60 bg-background/80">
                  <pillar.icon className="size-5 text-stronom-design" />
                </span>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <h3 className="text-base font-semibold text-foreground">{pillar.title}</h3>
                  <p>{pillar.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Button asChild className="shadow-[0_18px_60px_rgba(0,179,255,0.25)]">
              <a href="mailto:contact@stronom.com?subject=STRONOM%20Core%20Briefing">Request Core Briefing</a>
            </Button>
            <Button variant="outline" className="border-border/60" asChild>
              <a href="#suite">Explore Modules</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Core;
